import { FilterOperator, RoomStatus, useAdminSearchRoomsQuery } from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  type DataGridProps,
  type GridEventListener,
  type GridRowParams,
  NoRowsOverlay,
  PlaceOutlinedIcon,
} from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';

import { BaseExamLocationDataGrid } from '../../components';
import { useAdminCancelledLocationListSearchParams } from '../../hooks';
import { type CancelledExamLocationRow } from '../../types';

import { adminCancelledLocationColumns } from './columns';

export default function AdminCancelledLocationList() {
  const navigate = useNavigate();

  const [
    { page, pageSize, sortOrder, locationName, provinceId },
    { changePageNumber, changePageSize, changeSorting },
  ] = useAdminCancelledLocationListSearchParams();

  const { data: searchRoomsData, loading } = useAdminSearchRoomsQuery({
    variables: {
      searchRoomsInput: {
        paging: {
          pageSize,
          currentPage: page,
        },
        order: {
          field: 'retiredAt',
          type: ORDER_TYPE[sortOrder],
        },
        filter: {
          searchText: {
            fields: ['location'],
            value: locationName,
          },
          conditions: [
            {
              field: 'status',
              operator: FilterOperator.Equal,
              value: RoomStatus.Retired,
            },
            {
              field: 'provinceId',
              operator: FilterOperator.Equal,
              value: provinceId,
            },
          ],
        },
      },
    },
  });

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<CancelledExamLocationRow>,
  ) => {
    navigate('/admin/exam-location/locations/:roomId', {
      params: {
        roomId: params.row.id,
      },
    });
  };

  const handleSortModelChange: DataGridProps['onSortModelChange'] = (sortModel) => {
    const sort = sortModel[0]?.sort ?? 'asc';
    changeSorting({ sortOrder: sort });
  };

  const rows: CancelledExamLocationRow[] =
    searchRoomsData?.ecAdminSearchRooms.rooms.map((room) => ({
      id: room.id,
      locationName: room.building.location.nameTh ?? '',
      province: room.building.location.province.nameTh,
      buildingName: room.building.nameTh,
      roomName: room.name,
      floor: room.floor,
      roomNumber: room.roomNumber ?? '-',
      retiredAt: room.retiredAt ? new Date(room.retiredAt) : null,
      retiredRemark: room.retiredRemark ?? null,
      retiredBy: room.retiredByUser
        ? `${room.retiredByUser.firstname} ${room.retiredByUser.lastname}`
        : '-',
    })) ?? [];
  const total = searchRoomsData?.ecAdminSearchRooms.total;
  const isSearchResultEmpty = total === 0 && (locationName !== '' || provinceId !== '');

  const shouldHideFooter = total === 0 || loading;
  return (
    <BaseExamLocationDataGrid
      rows={rows}
      columns={adminCancelledLocationColumns}
      hideFooter={shouldHideFooter}
      onRowClick={handleRowClick}
      sortingMode='server'
      initialState={{
        sorting: {
          sortModel: [{ field: 'retiredAt', sort: sortOrder }],
        },
      }}
      onSortModelChange={handleSortModelChange}
      loading={loading}
      slotProps={{
        footer: {
          total: total ?? 0,
          pagination: {
            page: page,
            count: total ?? 0,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              const pageSize = parseInt(event.target.value);
              changePageNumber(0);
              return changePageSize(pageSize);
            },
          },
        },
      }}
      slots={{
        noRowsOverlay: () =>
          !isSearchResultEmpty ? (
            <NoRowsOverlay
              icon={PlaceOutlinedIcon}
              title='ยังไม่มีข้อมูลสถานที่สอบถูกยกเลิกการใช้งาน'
            />
          ) : (
            <NoRowsOverlay />
          ),
      }}
    />
  );
}
